import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Building2, Users, ChevronRight, Mail, Shield, Briefcase, DollarSign, TrendingUp, Award, Zap, Globe, CheckCircle, Star, ArrowRight } from 'lucide-react';

const Partnerships = () => {
  const handleContactClick = (type: string) => {
    window.location.href = `mailto:partnerships@thepeoapp.com?subject=${encodeURIComponent(type + ' Partnership Inquiry')}&body=${encodeURIComponent('I am interested in learning more about the ' + type + ' partnership opportunity with The PEO App.')}`;
  };

  const handleApplyClick = (type: string) => {
    window.location.href = `mailto:partnerships@thepeoapp.com?subject=${encodeURIComponent(type + ' Application')}&body=${encodeURIComponent('I would like to apply for the ' + type + ' program. Please provide me with the application form and next steps.')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-green-50/30 to-blue-50/20">
      <Helmet>
        <title>Partnership Programs | The PEO App - Grow Your Business</title>
        <meta name="description" content="Join The PEO App's world-class partner network. Earn up to 15% commissions, access qualified leads, and grow your business with our proven partnership programs." />
        <link rel="canonical" href="https://thepeoapp.com/partnerships" />
      </Helmet>
      
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Award className="h-4 w-4" />
              Trusted by 500+ Partners Worldwide
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Accelerate Your Growth with <br />
              <span className="text-primary">World-Class Partnerships</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Join our elite network of partners and unlock unprecedented growth opportunities. 
              From PEO providers to referral partners, we offer multiple pathways to success.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">523+</div>
                <div className="text-sm text-gray-600">Vetted PEOs</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">15%</div>
                <div className="text-sm text-gray-600">Max Commission</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">50K+</div>
                <div className="text-sm text-gray-600">Qualified Leads</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">$2.4M+</div>
                <div className="text-sm text-gray-600">Partner Earnings</div>
              </div>
            </div>
          </motion.div>

          {/* Partnership Programs Grid */}
          <div className="grid lg:grid-cols-2 gap-8 mb-20">
            {/* PEO Provider Program */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-primary/10 p-3 rounded-2xl">
                  <Building2 className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">PEO Provider Program</h2>
                  <p className="text-primary font-medium">Vetted Network Access</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                Join our exclusive network of 523+ vetted PEO providers. Get direct access to businesses 
                actively seeking PEO solutions with streamlined proposals and dedicated support.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900">Quality-First Vetting Process</span>
                    <p className="text-sm text-gray-600">Rigorous screening ensures only top-tier providers join our network</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900">Pre-Qualified Leads</span>
                    <p className="text-sm text-gray-600">Access businesses with verified needs and budget authority</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900">Streamlined Process</span>
                    <p className="text-sm text-gray-600">Automated proposal system reduces time-to-quote by 75%</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900">Performance Analytics</span>
                    <p className="text-sm text-gray-600">Real-time dashboards and conversion tracking</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <span className="font-medium text-gray-900">Success Metrics</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Avg. Lead Quality:</span>
                    <span className="font-medium text-gray-900 ml-1">92%</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Conversion Rate:</span>
                    <span className="font-medium text-gray-900 ml-1">35%</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleApplyClick('PEO Provider')}
                  className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
                >
                  Apply Now
                </button>
                <button
                  onClick={() => handleContactClick('PEO Provider')}
                  className="px-6 py-3 border border-gray-300 hover:border-primary text-gray-700 hover:text-primary font-medium rounded-xl transition-colors"
                >
                  Learn More
                </button>
              </div>
            </motion.div>

            {/* Referral Partner Program */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-primary/10 p-3 rounded-2xl">
                  <DollarSign className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Referral Partner Program</h2>
                  <p className="text-primary font-medium">Up to 15% Commission</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                Monetize your client relationships with our industry-leading referral program. 
                Earn substantial commissions while providing valuable PEO solutions to your network.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900">Tiered Commission Structure</span>
                    <p className="text-sm text-gray-600">5-15% based on volume, with lifetime value payments</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900">Dedicated Partner Manager</span>
                    <p className="text-sm text-gray-600">Personal support and strategic guidance for growth</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900">Marketing Materials</span>
                    <p className="text-sm text-gray-600">Professional assets, co-branded content, and sales tools</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900">Fast Payment Terms</span>
                    <p className="text-sm text-gray-600">Monthly payouts with transparent tracking</p>
                  </div>
                </div>
              </div>

              <div className="bg-primary/10 rounded-2xl p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="h-4 w-4 text-primary" />
                  <span className="font-medium text-gray-900">Earning Potential</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Avg. Monthly:</span>
                    <span className="font-medium text-gray-900 ml-1">$4,800</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Top Earner:</span>
                    <span className="font-medium text-gray-900 ml-1">$28k/mo</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleApplyClick('Referral Partner')}
                  className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
                >
                  Apply Now
                </button>
                <button
                  onClick={() => handleContactClick('Referral Partner')}
                  className="px-6 py-3 border border-gray-300 hover:border-primary text-gray-700 hover:text-primary font-medium rounded-xl transition-colors"
                >
                  Learn More
                </button>
              </div>
            </motion.div>
          </div>

          {/* Additional Programs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-20"
          >
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">Additional Partnership Opportunities</h2>
            <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
              Explore specialized partnership programs designed for specific industries and business models.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Strategic Partners */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="h-6 w-6 text-primary" />
                  <h3 className="text-xl font-semibold text-gray-900">Strategic Partners</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Enterprise-level partnerships for organizations seeking co-marketing opportunities, 
                  integration partnerships, and white-label solutions.
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <span>Custom integration opportunities</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <span>Co-marketing and co-branding</span>
                </div>
                <button
                  onClick={() => handleContactClick('Strategic Partnership')}
                  className="w-full border border-primary text-primary hover:bg-primary/10 font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Explore Opportunities
                </button>
              </div>

              {/* Technology Partners */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <Zap className="h-6 w-6 text-primary" />
                  <h3 className="text-xl font-semibold text-gray-900">Technology Partners</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  API partnerships and technology integrations for HR software providers, 
                  payroll companies, and workforce management platforms.
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <span>API access and documentation</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <span>Technical integration support</span>
                </div>
                <button
                  onClick={() => handleContactClick('Technology Partnership')}
                  className="w-full border border-primary text-primary hover:bg-primary/10 font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  View API Docs
                </button>
              </div>
            </div>
          </motion.div>

          {/* Partner Success Stories */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mb-20"
          >
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Partner Success Stories</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Regional PEO Provider</h4>
                    <p className="text-sm text-gray-600">Southeast US</p>
                  </div>
                </div>
                <blockquote className="text-gray-600 italic mb-4">
                  "Since joining The PEO App network, we've seen a 340% increase in qualified leads 
                  and reduced our customer acquisition cost by 60%."
                </blockquote>
                <div className="text-sm text-primary font-medium">
                  +340% Lead Increase
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Benefits Consultant</h4>
                    <p className="text-sm text-gray-600">Insurance Brokerage</p>
                  </div>
                </div>
                <blockquote className="text-gray-600 italic mb-4">
                  "The referral program has become a significant revenue stream. We earned over 
                  $85k last year just from PEO referrals to our existing clients."
                </blockquote>
                <div className="text-sm text-primary font-medium">
                  $85k Annual Earnings
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">HR Technology Firm</h4>
                    <p className="text-sm text-gray-600">Strategic Partner</p>
                  </div>
                </div>
                <blockquote className="text-gray-600 italic mb-4">
                  "The API integration was seamless, and we've been able to offer PEO matching 
                  as a value-add service to our 2,000+ clients."
                </blockquote>
                <div className="text-sm text-primary font-medium">
                  2,000+ Clients Served
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="text-center bg-gradient-to-br from-primary to-primary/80 rounded-3xl p-12 text-white"
          >
            <h2 className="text-4xl font-bold mb-4">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
              Join the ranks of successful partners who have accelerated their growth with The PEO App. 
              Choose your partnership path and start earning today.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <button
                onClick={() => handleApplyClick('Partnership')}
                className="bg-white text-primary hover:bg-gray-50 font-semibold px-8 py-4 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <Mail className="h-5 w-5" />
                Apply Now
              </button>
              <button
                onClick={() => handleContactClick('Partnership Information')}
                className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                Get Information
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-8 pt-8 border-t border-white/20">
              <p className="text-sm opacity-75">
                Questions? Email us at <a href="mailto:partnerships@thepeoapp.com" className="underline hover:no-underline">partnerships@thepeoapp.com</a> or schedule a call with our team.
              </p>
            </div>
          </motion.div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Partnerships;
